var m = require('mithril');
var Model = require('../../libs/Model');
var prop = require('mithril/stream');
var formGroup = require('../../components/formGroup');
var loaders = require('../../layout/components/loaders');
var modal = require('../../layout/components/modal');
var choices = require('../../libs/choices');
var renderWidthSelector = require('../../components/widthSelector');
var equipmentSelector = require('../../components/equipmentSelector');
var locationSelector = require('../../components/locationSelector');
var equipmentClassTree  = require('../../equipment/models/equipment');
var formDataFields = require('../../libs/formDataFields');
var SMART_ELEMENT_TYPES = ['smartTable', 'smartList', 'smartchart'];
var EQUIPMENT_DATA_ELEMENT_TYPES = ['list', 'chart', 'gauge', 'text'];
var NIMROD_BLUE = '#58abe2';
var NIMROD_RED = '#ff395a';
var NIMROD_YELLOW = '#cccc52';
var NIMROD_GREEN = '#00e98f';
var NIMROD_WHITE = '#f4f4f4';
var NIMROD_GRAY_LIGHT = '#c8c8c8';
var NIMROD_GRAY = '#9a9a9a';
var NIMROD_GRAY_DARK = '#6f6f6f';
var IMAGE_FORM_SKIP_FIELDS = ['@rid', 'srcPath', 'path'];
var SMART_TABLE_SOURCE_MODES = {
  CLASSES: 'classes',
  CUSTOM_EQUIPMENT: 'customEquipment'
};
var SMART_TABLE_SOURCE_OPTIONS = [
  {
    value: SMART_TABLE_SOURCE_MODES.CLASSES,
    label: 'Equipment Classes',
    description: 'Find rows by class under the location context.'
  },
  {
    value: SMART_TABLE_SOURCE_MODES.CUSTOM_EQUIPMENT,
    label: 'Specific Equipment',
    description: 'Use only the equipment RIDs listed here.'
  }
];
var DIAGRAM_SOURCE_EQUIPMENT_ERROR = 'Diagram Source must reference an equipment record.';

var ELEMENT_FIELD_HELP = {
  locationContextRid: 'Leaving this blank will display child equipment of this location or enter a location RID to use some other location.',
  smartChartEquipmentClasses: 'Choose the classes this Smart Chart will show points for.',
  smartChartDataField: 'Choose the timeseries rollup ().',
  smartTableSourceMode: 'Choose whether Smart Table rows come from class rules or an explicit equipment RID list.',
  smartTableEntityClasses: 'Choose the equipment or location classes this Smart Table should list.',
  smartTableCustomEquipment: 'Use only these equipment RIDs for the Smart Table. Separate multiple RIDs with commas.',
  smartTableIncludeChildClasses: 'When set to Yes, selected classes also include their subclasses.',
  smartTableComponentDepth: 'Choose how many child-component levels to include below each matching equipment.',
  smartTableInstanceLabelMode: 'Choose whether child component labels use the full name or a shorter suffix.'
};

var ELEMENT_TYPE_METADATA = {
  list: {
    label: 'List',
    accent: NIMROD_GREEN,
    secondary: NIMROD_YELLOW,
    emphasis: NIMROD_BLUE
  },
  chart: {
    label: 'Chart',
    accent: NIMROD_BLUE,
    secondary: NIMROD_YELLOW,
    emphasis: NIMROD_GREEN
  },
  gauge: {
    label: 'Gauge',
    accent: NIMROD_GREEN,
    secondary: NIMROD_BLUE,
    emphasis: NIMROD_WHITE
  },
  text: {
    label: 'Text',
    accent: NIMROD_BLUE,
    secondary: NIMROD_GREEN,
    emphasis: NIMROD_RED
  },
  table: {
    label: 'Table',
    accent: NIMROD_BLUE,
    secondary: NIMROD_GREEN,
    emphasis: NIMROD_YELLOW
  },
  smartTable: {
    label: 'Smart Table',
    accent: NIMROD_GREEN,
    secondary: NIMROD_BLUE,
    emphasis: NIMROD_YELLOW
  },
  smartList: {
    label: 'Smart List',
    accent: NIMROD_GREEN,
    secondary: NIMROD_YELLOW,
    emphasis: NIMROD_RED
  },
  smartchart: {
    label: 'Smart Chart',
    accent: NIMROD_BLUE,
    secondary: NIMROD_GREEN,
    emphasis: NIMROD_YELLOW
  },
  diagram: {
    label: 'Diagram',
    accent: NIMROD_YELLOW,
    secondary: NIMROD_BLUE,
    emphasis: NIMROD_GREEN
  },
  spacer: {
    label: 'Spacer',
    accent: NIMROD_RED,
    secondary: NIMROD_YELLOW,
    emphasis: NIMROD_GREEN
  },
  img: {
    label: 'Image',
    accent: NIMROD_RED,
    secondary: NIMROD_GREEN,
    emphasis: NIMROD_BLUE
  }
};

var roomClassTreeCache = null;
var roomClassTreePromise = null;

function loadRoomClassTree() {
  if (roomClassTreeCache) return Promise.resolve(roomClassTreeCache);
  if (roomClassTreePromise) return roomClassTreePromise;
  roomClassTreePromise = loaders.requestWithLoader({
    url: '/api/v1/ontology/classtree?type=room',
    method: 'GET',
    background: true
  })
  .then(tree => {
    roomClassTreeCache = tree || null;
    return roomClassTreeCache;
  })
  .catch(error => {
    console.error('Error loading room class tree:', error);
    return null;
  })
  .finally(() => {
    roomClassTreePromise = null;
  });
  return roomClassTreePromise;
}

function normalizeSingleRid(rawValue) {
  var trimmed;
  if (typeof rawValue === 'string') {
    trimmed = rawValue.trim();
  } else if (rawValue != null) {
    trimmed = String(rawValue).trim();
  } else {
    trimmed = '';
  }

  if (!trimmed) {
    return { normalized: null, hasValue: false, valid: true };
  }

  var prefixed = trimmed.charAt(0) === '#' ? trimmed : ('#' + trimmed);
  var valid = /^#\d+:\d+$/.test(prefixed);

  return { normalized: valid ? prefixed : null, hasValue: true, valid: valid };
}

function collectEquipmentClassNames(node, classNames) {
  if (!node || !node.name) return;
  var hasChildren = Array.isArray(node.children) && node.children.length > 0;
  if (node.data || hasChildren) {
    classNames.push(node.name);
  }
  if (hasChildren) {
    node.children.forEach(function (child) {
      collectEquipmentClassNames(child, classNames);
    });
  }
}

function getEquipmentClassNames(classTree) {
  var classNames = [];
  ((classTree && classTree.categories) || []).forEach(function (category) {
    collectEquipmentClassNames(category, classNames);
  });
  return classNames;
}

function validateDiagramEquipmentRid(equipmentRidState) {
  return equipmentClassTree.getClassTree()
  .then(function (classTree) {
    return loaders.requestWithLoader({
      loader: 'submit',
      method: 'GET',
      url: '/api/v1/equipment/' + equipmentRidState.normalized.slice(1)
    })
    .then(function (record) {
      return Boolean(
        record &&
        record['@class'] &&
        getEquipmentClassNames(classTree).indexOf(record['@class']) !== -1
      );
    })
    .catch(function () {
      return false;
    });
  })
  .catch(function () {
    return false;
  });
}

function normalizeLocationContextRid(rawValue) {
  return normalizeSingleRid(rawValue);
}

function renderFieldHelpIcon(helpText) {
  if (!helpText) {
    return null;
  }
  return m('span.element-field-info-icon', {
    'aria-label': helpText,
    'data-tooltip': helpText,
    role: 'img',
    tabindex: 0,
    title: helpText
  }, [
    m('span', { 'aria-hidden': 'true' }, 'i')
  ]);
}

function renderFieldLabel(label, helpText, attrs) {
  return m('div.element-field-label', [
    m('name', attrs || {}, label),
    renderFieldHelpIcon(helpText)
  ]);
}

function textValue(value) {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return value == null ? '' : String(value);
}

function hasSelectedSource(src) {
  if (!src) return false;
  var value = src();
  return value !== null &&
    typeof value !== 'undefined' &&
    value !== '' &&
    value !== 'null' &&
    value !== 'undefined';
}

function normalizeSmartTableSourceMode(value) {
  return value === SMART_TABLE_SOURCE_MODES.CUSTOM_EQUIPMENT
    ? SMART_TABLE_SOURCE_MODES.CUSTOM_EQUIPMENT
    : SMART_TABLE_SOURCE_MODES.CLASSES;
}

function smartTableCustomEquipmentValue() {
  if (!element.form || !element.form.customEquipment) {
    return '';
  }
  return textValue(element.form.customEquipment()).trim();
}

function inferSmartTableSourceMode() {
  var savedMode = element.form && element.form.smartTableSourceMode
    ? element.form.smartTableSourceMode()
    : null;
  if (
    savedMode === SMART_TABLE_SOURCE_MODES.CLASSES ||
    savedMode === SMART_TABLE_SOURCE_MODES.CUSTOM_EQUIPMENT
  ) {
    return savedMode;
  }
  return smartTableCustomEquipmentValue()
    ? SMART_TABLE_SOURCE_MODES.CUSTOM_EQUIPMENT
    : SMART_TABLE_SOURCE_MODES.CLASSES;
}

function getSmartTableSourceMode() {
  var sourceMode = inferSmartTableSourceMode();
  if (element.form && element.form.smartTableSourceMode && element.form.smartTableSourceMode() !== sourceMode) {
    element.form.smartTableSourceMode(sourceMode);
  }
  return sourceMode;
}

function setSmartTableSourceMode(sourceMode) {
  var normalizedMode = normalizeSmartTableSourceMode(sourceMode);
  if (element.form && element.form.smartTableSourceMode) {
    element.form.smartTableSourceMode(normalizedMode);
  }
  if (normalizedMode === SMART_TABLE_SOURCE_MODES.CLASSES) {
    element.form.customEquipment('');
  } else {
    element.form.equipmentClasses([]);
  }
}

function renderSmartTableSourceSelector() {
  var selectedMode = getSmartTableSourceMode();
  return m('div.smart-table-source-selector', SMART_TABLE_SOURCE_OPTIONS.map(function (option) {
    var selected = selectedMode === option.value;
    return m('button.smart-table-source-option' + (selected ? ' is-selected' : ''), {
      type: 'button',
      'aria-pressed': selected ? 'true' : 'false',
      title: ELEMENT_FIELD_HELP.smartTableSourceMode,
      onclick: function () {
        setSmartTableSourceMode(option.value);
      }
    }, [
      m('span.smart-table-source-option-title', option.label),
      m('span.smart-table-source-option-description', option.description)
    ]);
  }));
}

function renderSmartTableSourceFields(smartTableSourceMode, entityClassesArray) {
  var usesClasses = smartTableSourceMode === SMART_TABLE_SOURCE_MODES.CLASSES;
  return m('div.smart-table-source-fields-wrapper', [
    m('div.smart-table-source-fields', {
      key: 'smart-table-source-fields-' + smartTableSourceMode
    }, usesClasses ? [
      m('div.form-group.mb2', [
        renderFieldLabel("Equipment & Location Classes", ELEMENT_FIELD_HELP.smartTableEntityClasses),
        m(choices, {
          data: entityClassesArray,
          value: element.form.equipmentClasses,
        })
      ]),
      m('div.form-group.mb2', [
        renderFieldLabel("Include Child Classes", ELEMENT_FIELD_HELP.smartTableIncludeChildClasses),
        m("select.form-control", {
          onchange: function(ev) {
            element.form.includeChildClasses(ev.target.value === 'true');
          },
          value: element.form.includeChildClasses() === false ? 'false' : 'true',
        }, [
          m("option", { value: 'true' }, "Yes"),
          m("option", { value: 'false' }, "No"),
        ])
      ]),
      m('div.form-group.mb2', [
        renderFieldLabel("Component Depth", ELEMENT_FIELD_HELP.smartTableComponentDepth),
        m("select.form-control", {
          onchange: function(ev) {
            element.form.componentDepthLimit(parseInt(ev.target.value, 10));
          },
          value: element.form.componentDepthLimit() || 1,
        }, [
          m("option", { value: 1 }, "Immediate Children"),
          m("option", { value: 2 }, "2 Levels"),
          m("option", { value: 3 }, "3 Levels"),
        ])
      ])
    ] : [
      m(equipmentSelector, {
        inputId: 'element-custom-equipment-input',
        value: element.form.customEquipment,
        errors: element.errors,
        multiple: true,
        invalidMessage: 'Invalid format for custom equipment. Please use the format "#<clusterId>:<recordId>, #<clusterId>:<recordId>, ..."',
        renderLabel: function (inputId) {
          return renderFieldLabel("Custom Equipment Rids", ELEMENT_FIELD_HELP.smartTableCustomEquipment, { for: inputId });
        }
      })
    ])
  ]);
}

function streamValue (value) {
  return typeof value === 'function' ? value() : value;
}

function formValue (field) {
  return streamValue(streamValue(field));
}

function ridValue (field) {
  var value = formValue(field);
  return value && value['@rid'] ? value['@rid'] : value;
}

function isEquipmentDataElementType(type) {
  return EQUIPMENT_DATA_ELEMENT_TYPES.indexOf(type) !== -1;
}

function presentationType(type) {
  if (type === 'graphic' || type === 'image') return 'img';
  return type;
}

function getElementTypeLabel(type) {
  var metadata = ELEMENT_TYPE_METADATA[presentationType(type)] || {};
  return metadata.label || type || 'Element';
}

function getElementTypeOptions(smartElementsRestricted) {
  var options = [
    { value: 'list' },
    { value: 'chart' },
    { value: 'gauge' },
    { value: 'text' },
    { value: 'table' }
  ];

  if (!smartElementsRestricted) {
    options.push({ value: 'smartTable' });
    options.push({ value: 'smartList' });
    options.push({ value: 'smartchart' });
  }

  options.push({ value: 'diagram' });
  options.push({ value: 'spacer' });
  options.push({ value: 'img' });

  return options;
}

function normalizeTypeOption(option) {
  var metadata = ELEMENT_TYPE_METADATA[presentationType(option.value)] || {};
  return Object.assign({}, metadata, option, {
    label: option.label || metadata.label || option.value,
    isEquipmentDataElement: isEquipmentDataElementType(option.value),
    accent: option.accent || metadata.accent || NIMROD_BLUE,
    secondary: option.secondary || metadata.secondary || NIMROD_YELLOW,
    emphasis: option.emphasis || metadata.emphasis || NIMROD_RED
  });
}

function iconSvg(children) {
  return m('svg.element-type-tile-icon', {
    viewBox: '0 0 96 72',
    role: 'img',
    focusable: 'false',
    'aria-hidden': 'true'
  }, children);
}

function renderListIcon() {
  return iconSvg([
    m('rect', { x: 18, y: 16, width: 28, height: 10, rx: 1, fill: NIMROD_GRAY_LIGHT }),
    m('rect', { x: 52, y: 16, width: 26, height: 10, rx: 1, fill: 'var(--element-type-soft)' }),
    m('rect', { x: 18, y: 31, width: 28, height: 10, rx: 1, fill: NIMROD_GRAY }),
    m('rect', { x: 52, y: 31, width: 20, height: 10, rx: 1, fill: NIMROD_GRAY_DARK }),
    m('rect', { x: 18, y: 46, width: 28, height: 10, rx: 1, fill: NIMROD_GRAY_LIGHT }),
    m('rect', { x: 52, y: 46, width: 30, height: 10, rx: 1, fill: 'var(--element-type-soft)' })
  ]);
}

function renderChartIcon() {
  return iconSvg([
    m('path', {
      d: 'M16 58 H82',
      stroke: 'var(--element-type-soft)',
      'stroke-width': 4,
      'stroke-linecap': 'round'
    }),
    m('path', {
      d: 'M18 48 L34 38 L49 43 L64 24 L80 16',
      fill: 'none',
      stroke: NIMROD_GRAY_LIGHT,
      'stroke-width': 6,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    }),
    m('circle', { cx: 18, cy: 48, r: 4, fill: 'var(--element-type-accent)' }),
    m('circle', { cx: 34, cy: 38, r: 4, fill: NIMROD_GRAY }),
    m('circle', { cx: 49, cy: 43, r: 4, fill: 'var(--element-type-accent)' }),
    m('circle', { cx: 64, cy: 24, r: 4, fill: NIMROD_GRAY }),
    m('circle', { cx: 80, cy: 16, r: 4, fill: 'var(--element-type-accent)' })
  ]);
}

function renderGaugeIcon() {
  return iconSvg([
    m('path', {
      d: 'M24 52 A24 24 0 0 1 72 52',
      fill: 'none',
      stroke: NIMROD_BLUE,
      'stroke-width': 10,
      'stroke-linecap': 'round'
    }),
    m('path', {
      d: 'M24 52 A24 24 0 0 1 48 28',
      fill: 'none',
      stroke: NIMROD_GRAY_LIGHT,
      'stroke-width': 10,
      'stroke-linecap': 'round'
    }),
    m('path', {
      d: 'M48 52 L64 34',
      stroke: NIMROD_WHITE,
      'stroke-width': 5,
      'stroke-linecap': 'round'
    }),
    m('circle', { cx: 48, cy: 52, r: 6, fill: NIMROD_WHITE })
  ]);
}

function renderTextIcon() {
  return iconSvg([
    m('rect', { x: 20, y: 15, width: 34, height: 8, fill: NIMROD_WHITE }),
    m('rect', { x: 33, y: 23, width: 8, height: 34, fill: NIMROD_WHITE }),
    m('rect', { x: 52, y: 32, width: 24, height: 5, fill: 'var(--element-type-soft)' }),
    m('rect', { x: 52, y: 44, width: 20, height: 5, fill: NIMROD_BLUE }),
    m('rect', { x: 52, y: 56, width: 28, height: 5, fill: 'var(--element-type-soft)' })
  ]);
}

function renderTableIcon() {
  var cells = [];
  [17, 34, 51].forEach(function (y, rowIndex) {
    [20, 38, 56].forEach(function (x, colIndex) {
      cells.push(m('rect', {
        x: x,
        y: y,
        width: 16,
        height: 13,
        rx: 1,
        fill: rowIndex === 0 ? NIMROD_GRAY_LIGHT : (colIndex === 0 ? NIMROD_GRAY : 'var(--element-type-soft)')
      }));
    });
  });
  return iconSvg(cells);
}

function renderSmartTableIcon() {
  var cells = [];
  [24, 38, 52].forEach(function (y, rowIndex) {
    [20, 38, 56].forEach(function (x, colIndex) {
      cells.push(m('rect', {
        x: x,
        y: y,
        width: 16,
        height: 11,
        rx: 1,
        fill: rowIndex === 0 ? NIMROD_GRAY_LIGHT : (colIndex === 0 ? NIMROD_GRAY : 'var(--element-type-soft)')
      }));
    });
  });

  return iconSvg(cells.concat([
    m('path', {
      d: 'M46 7 V13 M28 13 H64 M28 13 V30 M46 13 V30 M64 13 V30',
      fill: 'none',
      stroke: 'var(--element-type-soft)',
      'stroke-width': 4,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    }),
    m('circle', { cx: 28, cy: 13, r: 4.5, fill: NIMROD_GRAY }),
    m('circle', { cx: 46, cy: 13, r: 4.5, fill: NIMROD_BLUE }),
    m('circle', { cx: 64, cy: 13, r: 4.5, fill: NIMROD_GRAY })
  ]));
}

function renderSmartListIcon() {
  return iconSvg([
    m('path', {
      d: 'M10 44 H18 M18 28 V58 M18 36 H36 M18 51 H36',
      fill: 'none',
      stroke: 'var(--element-type-soft)',
      'stroke-width': 4,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    }),
    m('circle', { cx: 18, cy: 36, r: 4.5, fill: NIMROD_GRAY }),
    m('circle', { cx: 18, cy: 51, r: 4.5, fill: NIMROD_GRAY_DARK }),
    m('rect', { x: 36, y: 16, width: 24, height: 10, rx: 1, fill: NIMROD_GRAY_LIGHT }),
    m('rect', { x: 64, y: 16, width: 22, height: 10, rx: 1, fill: 'var(--element-type-soft)' }),
    m('rect', { x: 36, y: 31, width: 24, height: 10, rx: 1, fill: NIMROD_GRAY }),
    m('rect', { x: 64, y: 31, width: 16, height: 10, rx: 1, fill: NIMROD_GRAY_DARK }),
    m('rect', { x: 36, y: 46, width: 24, height: 10, rx: 1, fill: NIMROD_GRAY_LIGHT }),
    m('rect', { x: 64, y: 46, width: 26, height: 10, rx: 1, fill: 'var(--element-type-soft)' })
  ]);
}

function renderSmartChartIcon() {
  return iconSvg([
    m('circle', { cx: 22, cy: 21, r: 6, fill: NIMROD_BLUE }),
    m('circle', { cx: 22, cy: 48, r: 6, fill: NIMROD_GRAY }),
    m('path', {
      d: 'M28 21 H44 V58 M28 48 H44',
      fill: 'none',
      stroke: 'var(--element-type-soft)',
      'stroke-width': 4,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    }),
    m('path', {
      d: 'M44 58 H82',
      stroke: 'var(--element-type-soft)',
      'stroke-width': 4,
      'stroke-linecap': 'round'
    }),
    m('path', {
      d: 'M52 48 L60 38 L69 42 L78 24 L84 18',
      fill: 'none',
      stroke: NIMROD_GRAY_LIGHT,
      'stroke-width': 5,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    }),
    m('circle', { cx: 52, cy: 48, r: 3.5, fill: NIMROD_BLUE }),
    m('circle', { cx: 60, cy: 38, r: 3.5, fill: NIMROD_GRAY }),
    m('circle', { cx: 69, cy: 42, r: 3.5, fill: NIMROD_BLUE }),
    m('circle', { cx: 78, cy: 24, r: 3.5, fill: NIMROD_GRAY }),
    m('circle', { cx: 84, cy: 18, r: 3.5, fill: NIMROD_BLUE })
  ]);
}

function renderDiagramIcon() {
  return iconSvg([
    m('path', {
      d: 'M34 24 H62 M48 24 V46',
      fill: 'none',
      stroke: 'var(--element-type-soft)',
      'stroke-width': 5,
      'stroke-linecap': 'round'
    }),
    m('rect', { x: 18, y: 14, width: 22, height: 20, rx: 2, fill: NIMROD_GRAY_LIGHT }),
    m('rect', { x: 56, y: 14, width: 22, height: 20, rx: 2, fill: NIMROD_BLUE }),
    m('rect', { x: 37, y: 44, width: 22, height: 18, rx: 2, fill: NIMROD_GRAY })
  ]);
}

function renderSpacerIcon() {
  return iconSvg([
    m('rect', { x: 18, y: 20, width: 60, height: 7, rx: 1, fill: 'var(--element-type-soft)' }),
    m('rect', { x: 18, y: 45, width: 60, height: 7, rx: 1, fill: 'var(--element-type-soft)' }),
    m('path', {
      d: 'M48 34 V27 M48 27 L42 33 M48 27 L54 33',
      stroke: NIMROD_WHITE,
      'stroke-width': 4,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    }),
    m('path', {
      d: 'M48 38 V45 M48 45 L42 39 M48 45 L54 39',
      stroke: NIMROD_WHITE,
      'stroke-width': 4,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round'
    })
  ]);
}

function renderImageIcon() {
  return iconSvg([
    m('rect', { x: 18, y: 14, width: 60, height: 44, rx: 2, fill: 'none', stroke: 'var(--element-type-soft)', 'stroke-width': 4 }),
    m('circle', { cx: 61, cy: 26, r: 6, fill: NIMROD_YELLOW }),
    m('path', { d: 'M22 54 L39 36 L51 48 L59 39 L75 54 Z', fill: NIMROD_WHITE })
  ]);
}

function renderTypeIcon(type) {
  switch (presentationType(type)) {
    case 'list': return renderListIcon();
    case 'chart': return renderChartIcon();
    case 'gauge': return renderGaugeIcon();
    case 'text': return renderTextIcon();
    case 'table': return renderTableIcon();
    case 'smartTable': return renderSmartTableIcon();
    case 'smartList': return renderSmartListIcon();
    case 'smartchart': return renderSmartChartIcon();
    case 'diagram': return renderDiagramIcon();
    case 'spacer': return renderSpacerIcon();
    case 'img': return renderImageIcon();
    default: return renderTableIcon();
  }
}

function renderElementModalTitle(defaultTitle, record, options) {
  options = options || {};
  if (record && record.customModalTitle) {
    return defaultTitle;
  }

  var type = options.type || (record && record.type ? record.type : (element.form && element.form.type && element.form.type()));
  if (!type) {
    return defaultTitle;
  }

  var isExistingRecord = !!(record && record['@rid']);
  if (
    !isExistingRecord &&
    !options.showSelectedType &&
    element.typeSelectorExpanded &&
    element.typeSelectorExpanded()
  ) {
    return defaultTitle;
  }

  var option = normalizeTypeOption({ value: type });
  var action = isExistingRecord ? 'Modify' : 'Create New';
  var title = action + ' ' + getElementTypeLabel(type);
  var canChangeType = !isExistingRecord && typeof options.onClearType === 'function';

  return m('span.modal-element-title', [
    m('span.modal-element-title-text', title),
    m('span.modal-element-title-icon' + (canChangeType ? '.has-clear' : ''), {
      style: {
        '--element-type-accent': option.accent,
        '--element-type-secondary': option.secondary,
        '--element-type-emphasis': option.emphasis
      }
    }, [
      renderTypeIcon(type),
      canChangeType ? m('button.modal-element-title-clear', {
        type: 'button',
        title: 'Change element type',
        'aria-label': 'Change element type',
        onclick: function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
          options.onClearType();
        }
      }, [
        m('i.ion-close', { 'aria-hidden': 'true' })
      ]) : null
    ])
  ]);
}

function renderEquipmentDataBadge() {
  return m('span.element-type-data-badge', {
    title: 'Equipment Data Element',
    'aria-label': 'Equipment Data Element'
  }, [
    m('svg', {
      viewBox: '0 0 24 24',
      focusable: 'false',
      'aria-hidden': 'true'
    }, [
      m('path', {
        d: 'M6.5 7 H17.5 M8.8 10 L12 17.5 M15.2 10 L12 17.5',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': 2.4,
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }),
      m('circle', { cx: 6.5, cy: 7, r: 3.7, fill: 'currentColor' }),
      m('circle', { cx: 17.5, cy: 7, r: 3.7, fill: 'currentColor' }),
      m('circle', { cx: 12, cy: 17.5, r: 3.7, fill: 'currentColor' })
    ])
  ]);
}

function renderTypeTile(option, config) {
  var selectedType = config.selectedType;
  var selectedValue = typeof selectedType === 'function' ? selectedType() : selectedType;
  var selected = selectedValue === option.value;
  var disabled = !!config.disabled;
  var tileClass = 'element-type-tile' + (selected ? ' is-selected' : '');

  return m('button', {
    key: option.value,
    type: 'button',
    class: tileClass,
    'data-element-type': option.value,
    'aria-pressed': selected ? 'true' : 'false',
    disabled: disabled ? true : undefined,
    style: {
      '--element-type-accent': option.accent,
      '--element-type-secondary': option.secondary,
      '--element-type-emphasis': option.emphasis
    },
    onclick: function () {
      if (disabled) return;
      config.onselect(option.value);
    }
  }, [
    option.isEquipmentDataElement ? renderEquipmentDataBadge() : null,
    renderTypeIcon(option.value),
    m('span.element-type-tile-label', option.label)
  ]);
}

function renderTypeGrid(options, config) {
  return m('div.element-type-grid', options.map(function (option) {
    return renderTypeTile(option, config);
  }));
}

function renderTypeDivider(label) {
  return m('div.element-type-divider', [
    m('span', label)
  ]);
}

function compactChildren(children) {
  var compacted = [];
  (children || []).forEach(function (child) {
    if (Array.isArray(child)) {
      compacted = compacted.concat(compactChildren(child));
    } else if (child !== null && child !== undefined && child !== '') {
      compacted.push(child);
    }
  });
  return compacted;
}

function renderSection(label, children) {
  var visibleChildren = compactChildren(children);
  if (!visibleChildren.length) {
    return null;
  }
  return [renderTypeDivider(label)].concat(visibleChildren);
}

function isSmartElementType(type) {
  return SMART_ELEMENT_TYPES.indexOf(type) !== -1;
}

function renderTypeSelector(config) {
  var showEquipmentDataGroup = config.showEquipmentDataGroup !== false;
  var options = (config.options || getElementTypeOptions(config.smartElementsRestricted))
    .map(normalizeTypeOption)
    .map(function (option) {
      return showEquipmentDataGroup ? option : Object.assign({}, option, {
        isEquipmentDataElement: false
      });
    });
  var equipmentDataOptions = options.filter(function (option) {
    return option.isEquipmentDataElement;
  });
  var displayOptions = options.filter(function (option) {
    return !option.isEquipmentDataElement && !isSmartElementType(option.value);
  });
  var dynamicOptions = options.filter(function (option) {
    return isSmartElementType(option.value);
  });
  var sections = [];

  if (equipmentDataOptions.length) {
    sections.push({ label: 'Equipment Data Elements', options: equipmentDataOptions });
  }

  if (displayOptions.length) {
    sections.push({ label: 'Display Elements', options: displayOptions });
  }

  if (dynamicOptions.length) {
    sections.push({ label: 'Dynamic Elements', options: dynamicOptions });
  }

  if (sections.length <= 1) {
    return m('div.element-type-selector', [
      renderTypeGrid(options, config)
    ]);
  }

  return m('div.element-type-selector', sections.map(function (section) {
    return m('div.element-type-section', [
      renderTypeDivider(section.label),
      renderTypeGrid(section.options, config)
    ]);
  }));
}

var element = Object.create(Model);
var elementSaveFunction = element.save;
var elementInitFormFunction = element.initForm;

element.name = 'Element';
element['@class'] =  'Element'
element.url = 'element'
element.hideTypeSelect = false;
element.typeSelectorExpanded = prop(true);

element.initForm = function (record, options) {
  var result = elementInitFormFunction.call(element, record, options);
  element.typeSelectorExpanded(!(record && record['@rid']));
  return result;
};

element.fields = {
  parent: {
    default: null
  },
  labelStyle: {
    default: 'custom',
    validators: {
      presence: true,
    }
  },
  name: {
    default: null,
  },
  type: {
    default: 'table',
    validators: {
      presence : true, 
    }
  },
  width: {
    default: 1,
    validators: {
      presence : true, 
    }
  },
  dividerType: {
    default: 'blank'
  },
  label: {
    default: ''
  },
  chartSpan: {
    default: 24
  },
  chartResample: {
    default: 'hours'
  },
  smartChartValueField: {
    default: 'mean'
  },
  yMax: {
    default: undefined
  },
  yMin: {
    default: undefined
  },
  siPrefix: {
    default: 0
  },
  src: {
  },
  dataChunk: '',
  graphicComponent: {
    default: 'text',
  },
  preferredTableView:{
    type: 'text',
    hidden: true,
    default: ''
  },
  timeseriesSpan: {
    default: 24
  },
  timeseriesSampling: {
    default: 'hours'
  },
  useHasPartEdge: {
    default: false
  },
  useHasElementEdge: {
    default: false
  },
  markdownText: {
    default: ''
  },
  height: {
    default: null,
    hidden: true
  },
  helpText: {
    default: '',
    hidden: true
  },
  equipmentClasses: {
    default: [],
    hidden: true
  },
  customLocations: {
    default: [],
    hidden: true
  },
  customEquipment: {
    default: '',
    hidden: true
  },
  smartTableSourceMode: {
    default: null,
    hidden: true
  },
  includeChildClasses: {
    default: false,
    hidden: true
  },
  componentDepthLimit: {
    default: 1,
    hidden: true
  },
  locationContextRid: {
    default: '',
    hidden: true
  },
  rowLimit: {
    default: 10,
    hidden: true
  },
  rowOrder: {
    default: null,
    hidden: true
  },
  nameColumnWidth: {
    default: null,
    hidden: true
  },
  permissionClasses: {
    default: [],
    hidden: true
  },
  summaryFields: {
    default: null,
    hidden: true
  },
  hiddenFields: {
    default: null,
    hidden: true
  },
  smartListOrder: {
    default: null,
    hidden: true
  },
  instanceLabelMode: {
    default: null,
    hidden: true
  },
  equipmentRid: {
    default: null,
    hidden: true
  },
};

function viewOption(vnode, optionName) {
  return !!(vnode && vnode.attrs && vnode.attrs[optionName]);
}

function currentLabelStyle() {
  return (element.form.labelStyle && element.form.labelStyle()) || 'custom';
}

function renderElementNameField() {
  if (element.form.type() === 'spacer') {
    element.form.name('Spacer');
    return null;
  }

  const isSmartList = element.form.type() === 'smartList';
  if (isSmartList && currentLabelStyle() !== 'custom') {
    return null;
  }

  return m('div.form-group.mb2', [
    m('name', { for: 'element-name-input' }, 'Name'),
    m('input.form-control', {
      type: 'text',
      autocomplete: 'off',
      oninput: function (ev) { element.form.name(ev.target.value) },
      value: element.form.name(),
    })
  ]);
}

function renderElementLabelStyleField() {
  if (element.form.type() !== 'smartList') {
    return null;
  }

  return m('div.form-group.mb2', [
    m('name', { for: 'element-label-style-input' }, 'Label Style'),
    m('select.form-control', {
      onchange: function (ev) { element.form.labelStyle(ev.target.value) },
      value: currentLabelStyle(),
    }, [
      m('option', { value: 'custom' }, 'Custom'),
      m('option', { value: 'first_dataset_name' }, 'First Dataset Name'),
      m('option', { value: 'equipment_and_dataset' }, 'Equipment Name – Dataset Name'),
    ])
  ]);
}

function renderElementTypeField(smartElementsRestricted) {
  if (element.hideTypeSelect || (element.form['@rid'] && element.form['@rid']())) {
    return null;
  }

  if (!element.typeSelectorExpanded()) {
    return null;
  }

  return m("div.form-group.mb2", [
    m("name", {for:"element-type-input"}, "Type"),
    renderTypeSelector({
      selectedType: element.form.type,
      smartElementsRestricted: smartElementsRestricted,
      showEquipmentDataGroup: !!(element.record && element.record.useHasElementEdge),
      disabled: !!element.record['@rid'],
      onselect: function (type) {
        element.form.type(type);
        element.typeSelectorExpanded(false);
        modal.vm.title(renderElementModalTitle('Create New Element', element.record || {}, {
          showSelectedType: true,
          onClearType: function () {
            element.typeSelectorExpanded(true);
            modal.vm.title('Create New Element');
            m.redraw();
          }
        }));
      }
    })
  ]);
}

function renderElementWidthField() {
  if (element.form.type() === 'spacer') {
    element.form.width(4);
    return null;
  }

  return m("div.form-group.mb2", [
    m('span', [
      m("name", {for:"element-type-input"}, "Width"),
      renderWidthSelector({
        selectedWidth: element.form.width,
        onselect: function (width) {
          element.form.width(width);
        }
      })
    ])
  ]);
}

element.view = function (vnode) {
  if (!roomClassTreeCache && !roomClassTreePromise) {
    loadRoomClassTree().then(function () { m.redraw(); });
  }

  const equipmentClassesArray = [];    
  equipmentClassTree.equipmentClassTree.categories.forEach((category) => traverse(category, '', equipmentClassesArray));

  const locationClassesArray = [];
  if (roomClassTreeCache && Array.isArray(roomClassTreeCache.categories)) {
    roomClassTreeCache.categories.forEach((category) => traverse(category, '', locationClassesArray));
  }
  const entityClassesArray = mergeClassOptions(equipmentClassesArray, locationClassesArray);
  ensureClassOption(entityClassesArray, 'Building');
  ensureClassOption(entityClassesArray, 'Level');
  ensureClassOption(entityClassesArray, 'Room');
  const mainEquipmentClasses = equipmentClassTree.equipmentClassTree.categories.map((category) => {
    return {
      name: category.name,
      value: category.name,
    }
  });

  var smartElementsRestricted = element.record && element.record.smartElementCreationAllowed === false;

  if (smartElementsRestricted && SMART_ELEMENT_TYPES.indexOf(element.form.type()) !== -1) {
    element.form.type('table');
  }

  element.save = function() {
    const formData = new FormData();
    const { ['@rid']: rid, src } = element.form;
    const hasValidSrc = hasSelectedSource(src);
    var diagramEquipmentRidState = null;
    function persistElementSave() {
      if (rid && rid()) {
        if (hasValidSrc) {
          formDataFields.appendFormFields(formData, element.form, {
            skipFields: IMAGE_FORM_SKIP_FIELDS
          });
          return loaders.requestWithLoader({
            loader: 'submit',
            url: `/api/v1/element/${rid().slice(1)}/image`,
            method: 'PUT',
            body: formData
          });
        } else {
          return Model.save.call(element);
        }
      } else {
        if (hasValidSrc) {
          formDataFields.appendFormFields(formData, element.form, {
            skipFields: IMAGE_FORM_SKIP_FIELDS
          });
          return loaders.requestWithLoader({
            loader: 'submit',
            url: `/api/v1/${element.url}`,
            method: 'POST',
            body: formData
          });
        } else {
          return Model.save.call(element);
        }
      }
    }

    if (element.form.labelStyle && !element.form.labelStyle()) {
      element.form.labelStyle('custom');
    }
    if (element.form.type() === 'smartTable' ) {
      var smartTableSourceMode = getSmartTableSourceMode();
      if (element.form.customLocations()) {
        //Validate format of aditional locations
        const value = element.form.customLocations();
        if (value && value.length > 0) {
          const isValid = /^#\d+:\d+(,\s*#\d+:\d+)*$/.test(value);
          if (!isValid) {
            element.errors.push('Invalid format for aditional locations. Please use the format "#<clusterId>:<recordId>, #<clusterId>:<recordId>, ..."');
            return;
          }
        }
      }
      if (smartTableSourceMode === SMART_TABLE_SOURCE_MODES.CUSTOM_EQUIPMENT) {
        element.form.equipmentClasses([]);
        const value = smartTableCustomEquipmentValue();
        if (!value) {
          element.errors.push('Add at least one equipment RID.');
          return;
        }
        const isValid = /^#\d+:\d+(,\s*#\d+:\d+)*$/.test(value);
        if (!isValid) {
          element.errors.push('Invalid format for custom equipment. Please use the format "#<clusterId>:<recordId>, #<clusterId>:<recordId>, ..."');
          return;
        }
        element.form.customEquipment(value);
      } else {
        element.form.customEquipment('');
        const selectedClasses = element.form.equipmentClasses && element.form.equipmentClasses();
        if (!Array.isArray(selectedClasses) || selectedClasses.length === 0) {
          element.errors.push('Select at least one equipment or location class.');
          return;
        }
      }
    } else if (element.form.type() === 'smartList') {
      const value = (element.form.customEquipment && element.form.customEquipment()) || '';
      const singleRid = /^#\d+:\d+$/;
      if (!value || !singleRid.test(String(value).trim())) {
        element.errors.push('Invalid format for equipment Rid. Please use the format "#<clusterId>:<recordId>"');
        return;
      }
      if (String(value).includes(',')) {
        element.errors.push('Smart List accepts only one equipment Rid. Remove additional RIDs.');
        return;
      }
  } else if (element.form.type() === 'smartchart') {
    const selectedClasses = element.form.equipmentClasses && element.form.equipmentClasses();
    if (!Array.isArray(selectedClasses) || selectedClasses.length === 0) {
      element.errors.push('Select at least one equipment class for Smart Chart.');
      return;
    }
  } else if (element.form.type() === 'diagram') {
    const equipmentRidState = normalizeSingleRid(
      element.form.equipmentRid && element.form.equipmentRid()
    );
    if (!equipmentRidState.hasValue || !equipmentRidState.valid) {
      element.errors.push('Invalid format for equipment Rid. Please use the format "#<clusterId>:<recordId>"');
      return;
    }
    element.form.equipmentRid(equipmentRidState.normalized);
    diagramEquipmentRidState = equipmentRidState;
  }

    const locationContextState = normalizeLocationContextRid(
      element.form.locationContextRid && element.form.locationContextRid()
    );
    if (!locationContextState.valid) {
      element.errors.push('Invalid format for Location Context RID. Please use the format "#<clusterId>:<recordId>".');
      return;
    }
    element.form.locationContextRid(locationContextState.normalized);

    if (diagramEquipmentRidState) {
      return validateDiagramEquipmentRid(diagramEquipmentRidState)
      .then(function (isEquipmentRid) {
        if (!isEquipmentRid) {
          element.errors.push(DIAGRAM_SOURCE_EQUIPMENT_ERROR);
          m.redraw();
          return;
        }
        return persistElementSave();
      });
    }

    return persistElementSave();
  };
  
  var elementType = element.form.type && element.form.type();
  var viewOptionFields = [
    elementType === 'smartList' ? renderElementLabelStyleField() : null,
    (function () {
      if (['chart', 'smallchart', 'smartchart'].indexOf(elementType) === -1) {
        return null;
      }
      return m('div.form-group.mb2', [
        m("name", {for:"element-name-input"}, "Span"),
        m("input.form-control", {
          type:"number",
          autocomplete: 'off',
          oninput: function(ev) { element.form.chartSpan(ev.target.value) },
          value: element.form.chartSpan(),
        }),
        m("name", {for:"element-type-input"}, "Chart Resample"),
        m("select.form-control", {
          onchange: function(ev) { element.form.chartResample(ev.target.value) },
          value: element.form.chartResample(),
        }, [
          m("option", {value:"hour"}, "Hour"),
          m("option", {value:"hours"}, "Hours"),
          m("option", {value:"days"}, "Days"),
          m("option", {value:"minutes"}, "Minutes")
        ]),
        m(formGroup, [
          m('name', {for:'dashboard-yMax-input'}, 'Y Maximum'),
          m('input.form-control', {
            id:'dashboard-yMax-input',
            type:'number',
            oninput: function(ev) { element.form.yMax(ev.target.value) },
            value: element.form.yMax(),
          })
        ]),
        m(formGroup, [
          m('name', {for:'dashboard-yMin-input'}, 'Y Minimum'),
          m('input.form-control', {
            id:'dashboard-yMin-input',
            type:'number',
            oninput: function(ev) { element.form.yMin(ev.target.value) },
            value: element.form.yMin(),
          })
        ])
      ]);
    })(),
    (function () {
      if (elementType !== 'spacer') {
        return null;
      }
      return m('div.form-group.mb2', [
        m("name", {for:"element-type-input"}, "Display As "),
        m("select.form-control", {
          onchange: function(ev) { element.form.dividerType(ev.target.value) },
          value: element.form.dividerType(),
        }, [
          m("option", {value:"blank"}, "Hidden"),
          m("option", {value:"line"}, "Line")
        ])
      ]);
    })(),
    (function () {
      if (elementType !== 'text') {
        return null;
      }
      if (!element.form.height()) {
        element.form.height(2);
      }
      return m('div.form-group.mb2', [
        m("label", "Height"),
        m("select.form-control", {
          onchange: function(ev) { element.form.height(ev.target.value) },
          value: element.form.height(),
        }, [
          m("option", {value:1}, "None"),
          m("option", {value:2}, "Single"),
          m("option", {value:3}, "Double"),
          m("option", {value:4}, "Full")
        ])
      ]);
    })(),
    (function () {
      if (elementType !== 'smartTable') {
        return null;
      }
      var usesClasses = getSmartTableSourceMode() === SMART_TABLE_SOURCE_MODES.CLASSES;
      return [
        m('div.form-group.mb2', [
          m("name", "Row Limit"),
          m("select.form-control", {
            onchange: function(ev) { element.form.rowLimit(ev.target.value) },
            value: element.form.rowLimit(),
          },[
            m("option", {value: 10}, "10"),
            m("option", {value: 20}, "20"),
            m("option", {value: 30}, "30"),
            m("option", {value: 40}, "40"),
            m("option", {value: 50}, "50"),
          ])
        ]),
        m('div.form-group.mb2', [
          m("name", "Name Column Width (rem)"),
          m("input.form-control", {
            type: "number",
            min: 8,
            max: 40,
            step: 1,
            oninput: function(ev) {
              element.form.nameColumnWidth(ev.target.value === '' ? null : ev.target.value);
            },
            placeholder: 'Auto',
            value: element.form.nameColumnWidth() || '',
          })
        ]),
        usesClasses ? m('div.form-group.mb2', [
          renderFieldLabel("Instance Label Mode", ELEMENT_FIELD_HELP.smartTableInstanceLabelMode),
          m("select.form-control", {
            onchange: function(ev) {
              element.form.instanceLabelMode(ev.target.value);
            },
            value: element.form.instanceLabelMode() || 'suffix',
          }, [
            m("option", { value: 'suffix' }, "Suffix (split on '-' or space)"),
            m("option", { value: 'full' }, "Full name"),
          ])
        ]) : null,
        viewOption(vnode, 'hideWidthSelect') ? null : renderElementWidthField()
      ];
    })(),
    elementType === 'smartTable' || viewOption(vnode, 'hideWidthSelect') ? null : renderElementWidthField()
  ];
  var leadingFields = m("div", compactChildren([
    renderElementTypeField(smartElementsRestricted),
    viewOption(vnode, 'hideNameInput') ? null : renderElementNameField()
  ]));
  var deferTypeSpecificFields = viewOption(vnode, 'deferTypeSpecificFields') || (
    !element.hideTypeSelect &&
    element.typeSelectorExpanded &&
    element.typeSelectorExpanded() &&
    !(element.form['@rid'] && element.form['@rid']())
  );

  if (deferTypeSpecificFields) {
    return m("form", {role:"form"}, compactChildren([
      element.errorView(),
      leadingFields
    ]));
  }

  return m("form", {role:"form"}, compactChildren([
    element.errorView(),
    leadingFields,
    (function () {
      if (SMART_ELEMENT_TYPES.indexOf(elementType) === -1) {
        return null;
      }
      var isSmartTable = elementType === 'smartTable';
      return [
        isSmartTable ? renderTypeDivider('Location') : null,
        m(locationSelector, {
          inputId: 'element-location-context-input',
          value: element.form.locationContextRid,
          errors: element.errors,
          invalidMessage: 'Invalid format for Location Context RID. Please use the format "#<clusterId>:<recordId>".',
          renderLabel: function (inputId) {
            return isSmartTable
              ? null
              : renderFieldLabel('Location Context RID', ELEMENT_FIELD_HELP.locationContextRid, { for: inputId });
          }
        })
      ];
    })(),
    (function () {
      if (elementType !== 'smartList') {
        return null;
      }

      return m(equipmentSelector, {
        label: 'Equipment RID',
        value: element.form.customEquipment,
        errors: element.errors,
        invalidMessage: 'Invalid format for equipment Rid. Please use the format "#<clusterId>:<recordId>"',
        singleOnlyMessage: 'Smart List accepts only one equipment Rid. Remove additional RIDs.'
      });
    })(),
    (function () {
      if (elementType !== 'diagram') {
        return null;
      }

      return m(equipmentSelector, {
        inputId: 'element-equipment-rid-input',
        label: 'Diagram Source',
        value: element.form.equipmentRid,
        errors: element.errors,
        invalidMessage: 'Invalid format for equipment Rid. Please use the format "#<clusterId>:<recordId>"',
        requiredSelectionMessage: 'Diagram Element requires selecting a valid equipment.'
      });
    })(),
    (function () {
      if (elementType !== 'smartchart') {
        return null;
      }

      return [
        m('div.form-group.mb2', [
          renderFieldLabel('Equipment Classes', ELEMENT_FIELD_HELP.smartChartEquipmentClasses),
          m(choices, {
            data: equipmentClassesArray,
            value: element.form.equipmentClasses
          })
        ]),
        m('div.form-group.mb2', [
          renderFieldLabel('Data Field', ELEMENT_FIELD_HELP.smartChartDataField),
          m('select.form-control', {
            onchange: function (ev) { element.form.smartChartValueField(ev.target.value); },
            value: element.form.smartChartValueField()
          }, [
            m('option', { value: 'mean' }, 'Mean'),
            m('option', { value: 'min' }, 'Min'),
            m('option', { value: 'max' }, 'Max')
          ])
        ])
      ];
    })(),
    (function () {
      if (elementType === 'img' || elementType === 'graphic') {
        return m('div.form-group.mb2', [
          m("label", {for:"element-src-input"}, "Src"),
          m('input.form-control', {
            type: 'file',
            accept: 'image/*',
            style: {background: 'transparent', border: 'none'},
            onchange: function (e) {
                if (e.target.files && e.target.files[0]) {
                    var reader = new FileReader();
                    reader.onload = function (event) {
                        var imagePreview = document.getElementById('image-preview');
                        if (imagePreview) {
                            imagePreview.src = event.target.result;
                            imagePreview.style.display = 'block'; // Make the image visible
                        }
                        m.redraw(); // Force Mithril to update the DOM
                    }
                    reader.readAsDataURL(e.target.files[0]);
                    element.form.src(e.target.files[0]);
                }
            }
          }),
          element.record.srcPath ?
            m('img', {
                id: 'image-preview',
                src: element.form['@rid'] && element.form['@rid']() ?
                    `/api/v1/element/${element.form['@rid']().slice(1)}/image` : '',
                alt: 'Image Preview',
                style: { maxWidth: '200px', maxHeight: '200px', display: element.form['@rid'] && element.form['@rid']() ? 'block' : 'none' } // Hide initially if no image
            })
          :
            ''
        ]);
      }
    })(),
    (function () {
      if (elementType === 'spacer') {
        return m('div.form-group.mb2', [
          m("name", {for:"element-name-input"}, "Label"),
          m("input.form-control", {
            type:"text",
            autocomplete: 'off',
            oninput: function(ev) { element.form.label(ev.target.value) },
            value: element.form.label(),
          })
        ]);
      }
    })(),
    (function () {
      if (elementType === 'text') {
        return m('div.form-group.mb2', [
          m("label", "Markdown Text"),
          m('textarea', {
            resize: 'none',
            id:'comment',
            rows:'5',
            cols:'40',
            'box-sizing': 'border-box',
            placeholder:"Add your markdown text here...",
            oninput: function(ev) { element.form.markdownText(ev.target.value) },
            value: element.form.markdownText(),
          })
        ]);
      }
    })(),
    (function () {
      if (elementType === 'smartTable') {
        var smartTableSourceMode = getSmartTableSourceMode();
        return [
          renderTypeDivider('Equipment Source'),
          renderSmartTableSourceSelector(),
          renderSmartTableSourceFields(smartTableSourceMode, entityClassesArray)
        ]
      }
    })(),
    (function () {
      var locationRecord = formValue(element.form.location);
      if (!locationRecord || locationRecord['@class'] == 'Dashboard') return null;
      if (locationRecord['@rid'] != ridValue(element.form.parent)) return null;
      return [
        m('div.form-group.mb2', [
          m('name', 'Permission Classes'),
          m(choices, {
            data: mainEquipmentClasses,
            value: element.form.permissionClasses,
          })
        ]),
        m('div.form-group.mb2', [
          m('name', 'Help text'),
          m('textarea', {
            resize: 'none',
            id:'comment',
            rows:'5',
            cols:'40',
            'box-sizing': 'border-box',
            placeholder:"Add your help markdown text here...",
            oninput: function(ev) { element.form.helpText(ev.target.value) },
            value: element.form.helpText(),
          })
        ])
      ]
    })(),
    renderSection('View Options', viewOptionFields)
  ]))
};

function traverse(node, path, equipmentClassesArray) {
  const currentPath = path ? `${path} > ${node.name}` : node.name;
  equipmentClassesArray.push({
    value: node.name,
    key: currentPath,
  });

  // Recursively process children if they exist
  if (node.children && Array.isArray(node.children)) {
    node.children.forEach((child) => traverse(child, currentPath, equipmentClassesArray));
  }
}

function mergeClassOptions(primary, secondary) {
  var combined = [];
  var seen = new Set();
  [primary, secondary].forEach(list => {
    (list || []).forEach(option => {
      if (!option || !option.value || seen.has(option.value)) return;
      seen.add(option.value);
      combined.push(option);
    });
  });
  return combined;
}

function ensureClassOption(options, value) {
  if (!options || !value) return;
  if (options.find(option => option.value === value)) return;
  options.unshift({ value: value, key: value });
}

element.renderTypeSelector = renderTypeSelector;
element.renderSection = renderSection;
element.renderTypeDivider = renderTypeDivider;
element.renderTypeIcon = renderTypeIcon;
element.getElementTypeLabel = getElementTypeLabel;
element.modalTitle = renderElementModalTitle;
element.getElementTypeOptions = getElementTypeOptions;
element.equipmentDataElementTypes = EQUIPMENT_DATA_ELEMENT_TYPES;
element.renderNameField = renderElementNameField;
element.renderWidthField = renderElementWidthField;

module.exports = element;
